import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../data/supabaseClient';

const TABLE = { ride: 'rides', package: 'package_deliveries' };

/**
 * Fetches the LATEST flow row (ride or delivery) for a conversation and keeps it
 * live via a realtime subscription. We fetch the most recent row regardless of
 * status (rather than filtering terminals) because a conversation can accumulate
 * several rows once rebooking is allowed — filtering + maybeSingle() would either
 * return a stale row, miss a terminal outcome, or error on multiple matches.
 * FlowEngine decides what to render from the row's status (active card, terminal
 * outcome + "start new", or the review/start flow).
 *
 * @returns {{ row: object|null, loading: boolean, error: object|null, refetch: function }}
 */
export function useFlowRow(entity, conversationId) {
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const table = TABLE[entity];

  const fetchRow = useCallback(async () => {
    if (!table || !conversationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: err } = await supabase
      .from(table)
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (err && err.code !== 'PGRST116') {
      setError(err);
    } else {
      setRow(data || null);
      setError(null);
    }
    setLoading(false);
  }, [table, conversationId]);

  useEffect(() => {
    fetchRow();
  }, [fetchRow]);

  useEffect(() => {
    if (!table || !conversationId) return undefined;
    const channel = supabase
      .channel(`flow:${table}:${conversationId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table, filter: `conversation_id=eq.${conversationId}` },
        () => {
          fetchRow();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [table, conversationId, fetchRow]);

  return { row, loading, error, refetch: fetchRow };
}
